import React, { ReactElement, useContext, MutableRefObject } from 'react'

import FormRow from '../../Common/Components/FormRow'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import { DisplayedAsOptions as DisplayedAsOptionsLanguage, DisplayOptions } from '../../../../i18n'
import ChartFields from './ChartFields'
import { WallboardDisplayedAsValues } from '../../Common/Constants/ReportDisplayedAsValues'

// CSS imports. Commented out because of current build process.
// import './callStateThresholds.scss'

/**
 * Component properties type.
 */
type Props = {
    isPersonalDashboard: MutableRefObject<boolean>
    displayedAsWallboardReport: number
    setDisplayedAsWallboardReport: (value: number) => void
    chartType: number
    setChartType: (value: number) => void
    chartProvider: number
    setChartProvider: (value: number) => void
}

/**
 * Call state thresholds section in Alarms tab of report configuration modal window.
 *
 * @param isPersonalDashboard Is this personal dashboard mode?
 * @param displayedAsWallboardReport Selected display option for wallboard report.
 * @param setDisplayedAsWallboardReport Method that sets the selected display type.
 * @param chartType Selected chart style option for list reports.
 * @param setChartType Method that sets the selected chart style.
 * @param chartProvider Selected chart provider option for list reports.
 * @param setChartProvider Method that sets the selected chart provider.
 */
const WallboardDisplayOptions: React.FC<Props> = ({
    isPersonalDashboard,
    displayedAsWallboardReport,
    setDisplayedAsWallboardReport,
    chartType,
    setChartType,
    chartProvider,
    setChartProvider
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('DisplayOptions')
    language.loadBundle('DisplayedAsOptions')

    const nonPDOptions = (): SelectOptions<number> => {
        if (isPersonalDashboard.current) {
            return []
        }
        return [
            {
                label: `${language?.get(DisplayedAsOptionsLanguage.DisplayasoptionsChart)}`,
                value: WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_CHART
            }
        ]
    }

    const displayedAsWallboardReportOptions: SelectOptions<number> = [
        {
            label: `${language?.get(DisplayedAsOptionsLanguage.DisplayasoptionsGrid)}`,
            value: WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_GRID
        },
        {
            label: `${language?.get(DisplayedAsOptionsLanguage.DisplayasoptionsColumns)}`,
            value: WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_COLUMNS
        },
        {
            label: `${language?.get(DisplayedAsOptionsLanguage.DisplayasoptionsRows)}`,
            value: WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_ROWS
        },
        ...nonPDOptions()
    ]

    const isDisplayedAsChart = displayedAsWallboardReport === WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_CHART

    return (
        <React.Fragment>
            <FormRow className={'displayed-as'}>
                <Select
                    label={language?.get(DisplayOptions.DisplayedAs)}
                    options={displayedAsWallboardReportOptions}
                    value={displayedAsWallboardReport}
                    handleChange={setDisplayedAsWallboardReport}
                />
            </FormRow>
            {isDisplayedAsChart && (
                <ChartFields
                    chartType={chartType}
                    setChartType={setChartType}
                    chartProvider={chartProvider}
                    setChartProvider={setChartProvider}
                />
            )}
        </React.Fragment>
    )
}

export default WallboardDisplayOptions
